import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Plus,
  RotateCcw,
  Trash2,
  Shield,
  ShieldOff,
  X,
  Loader2,
  AlertTriangle,
  Check,
  Crown,
  Image,
  Video,
  Calendar,
  Mail,
  Clock,
  TrendingUp,
  UserCheck,
  UserX,
  CreditCard,
  Gift,
  AlertCircle,
  CheckCircle,
  Info,
  BarChart3
} from 'lucide-react';
import { adminUsersService } from '../../services/AdminUsersService';
import UsersAnalyticsDashboard from './UsersAnalyticsDashboard';

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const UsersManagement = () => {
  // Estados de dados
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Estados de filtro
  const [filters, setFilters] = useState({
    search: '',
    plan: '',
    status: '',
    is_admin: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Estados de UI
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estados de modais
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Estado de ação em progresso
  const [actionLoading, setActionLoading] = useState(null);

  // Estados de analytics
  const [activeView, setActiveView] = useState('listagem');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  const { helpers } = adminUsersService;

  // ============================================
  // CARREGAR DADOS
  // ============================================

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Converter is_admin para o formato correto
      // '' ou undefined = null (não filtrar)
      // 'true' = true (apenas admins)
      // 'false' = false (apenas não-admins)
      let isAdminFilter = null;
      if (appliedFilters.is_admin === 'true') {
        isAdminFilter = true;
      } else if (appliedFilters.is_admin === 'false') {
        isAdminFilter = false;
      }
      // Se for '' ou undefined, permanece null

      const result = await adminUsersService.listUsers({
        limit: pagination.limit,
        offset: pagination.offset,
        plan: appliedFilters.plan || null,
        status: appliedFilters.status || null,
        search: appliedFilters.search || null,
        is_admin: isAdminFilter,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      setUsers(result.data || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        hasMore: result.pagination?.has_more || false
      }));
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.limit, pagination.offset, appliedFilters, sortBy, sortOrder]);

  const loadStatistics = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const result = await adminUsersService.getStatistics();
      setStatistics(result.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setIsLoadingAnalytics(true);
    try {
      const result = await adminUsersService.getAnalytics();
      setAnalyticsData(result.data);
    } catch (err) {
      console.error('Erro ao carregar analytics:', err);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', plan: '', status: '', is_admin: '' });
    setAppliedFilters({});
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // ============================================
  // AÇÕES
  // ============================================

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleResetUsage = async (user) => {
    if (!window.confirm(`Tem certeza que deseja resetar o uso de ${user.name}?`)) return;

    setActionLoading(user.id);
    try {
      await adminUsersService.resetUsage(user.id);
      showSuccess(`Uso de ${user.name} resetado com sucesso!`);
      loadUsers();
    } catch (err) {
      setError(`Erro ao resetar uso: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (user) => {
    const action = user.is_admin ? 'remover admin de' : 'promover a admin';
    if (!window.confirm(`Tem certeza que deseja ${action} ${user.name}?`)) return;

    setActionLoading(user.id);
    try {
      await adminUsersService.toggleAdmin(user.id, !user.is_admin);
      showSuccess(`${user.name} ${user.is_admin ? 'removido de admin' : 'promovido a admin'}!`);
      loadUsers();
      loadStatistics();
    } catch (err) {
      setError(`Erro: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ============================================
  // RENDER - CARDS DE ESTATÍSTICAS
  // ============================================

  const renderStatsCards = () => {
    if (isLoadingStats) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      );
    }

    if (!statistics) return null;

    const stats = [
      {
        label: 'Total de Usuários',
        value: statistics.total_users,
        icon: Users,
        color: 'blue'
      },
      {
        label: 'Assinaturas Ativas',
        value: (statistics.users_by_status?.active || 0) + (statistics.users_by_status?.trialing || 0),
        icon: UserCheck,
        color: 'green'
      },
      {
        label: 'Imagens Geradas',
        value: statistics.total_usage?.images || 0,
        icon: Image,
        color: 'purple'
      },
      {
        label: 'Vídeos Gerados',
        value: statistics.total_usage?.videos || 0,
        icon: Video,
        color: 'pink'
      }
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className={`bg-white rounded-xl p-4 border border-gray-100 shadow-sm`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">{stat.label}</span>
              <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {helpers.formatNumber(stat.value)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  // ============================================
  // RENDER - FILTROS
  // ============================================

  const renderFilters = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Busca */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </div>

        {/* Botão de filtros avançados */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-colors ${
            showFilters ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-5 h-5" />
          Filtros
          {Object.values(appliedFilters).some(v => v) && (
            <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
          )}
        </button>

        {/* Botão de buscar */}
        <button
          onClick={handleApplyFilters}
          className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
        >
          <Search className="w-5 h-5" />
          Buscar
        </button>

        {/* Botão de atualizar */}
        <button
          onClick={() => { loadUsers(); loadStatistics(); }}
          className="p-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          title="Atualizar"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filtros avançados */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
            <select
              value={filters.plan}
              onChange={(e) => setFilters(prev => ({ ...prev, plan: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todos os planos</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="max">Max</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todos os status</option>
              <option value="active">Ativo</option>
              <option value="trialing">Trial</option>
              <option value="canceled">Cancelado</option>
              <option value="inactive">Inativo</option>
              <option value="past_due">Pagamento Pendente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={filters.is_admin}
              onChange={(e) => setFilters(prev => ({ ...prev, is_admin: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todos</option>
              <option value="true">Apenas Admins</option>
              <option value="false">Apenas Usuários</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={handleClearFilters}
              className="w-full px-4 py-2 text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // RENDER - TABELA DE USUÁRIOS
  // ============================================

  const renderUsersTable = () => {
    if (isLoading) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-4" />
            <p className="text-gray-500">Carregando usuários...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-red-800">Erro ao carregar</p>
            <p className="text-red-600">{error}</p>
          </div>
          <button
            onClick={loadUsers}
            className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum usuário encontrado</h3>
          <p className="text-gray-500">
            {Object.values(appliedFilters).some(v => v)
              ? 'Tente ajustar os filtros de busca.'
              : 'Ainda não há usuários cadastrados.'}
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                  Usuário
                </th>
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-gray-600 cursor-pointer hover:text-amber-600"
                  onClick={() => handleSort('subscription.plan')}
                >
                  Plano
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                  Uso
                </th>
                <th 
                  className="text-left px-6 py-4 text-sm font-semibold text-gray-600 cursor-pointer hover:text-amber-600"
                  onClick={() => handleSort('created_at')}
                >
                  Criado em
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => {
                const planInfo = helpers.formatPlan(user.subscription?.plan);
                const statusInfo = helpers.formatSubscriptionStatus(user.subscription?.status, user.subscription?.plan);
                const imagesUsed = user.usage?.images_generated || 0;
                const videosUsed = user.usage?.videos_generated || 0;

                return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    {/* Usuário */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${helpers.getAvatarColor(user.name)} flex items-center justify-center text-white font-semibold text-sm`}>
                          {helpers.getInitials(user.name)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">{user.name}</p>
                            {user.is_admin && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                Admin
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Plano */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-${planInfo.color}-100 text-${planInfo.color}-700`}>
                        {planInfo.emoji} {planInfo.text}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-700`}>
                        {statusInfo.text}
                      </span>
                    </td>

                    {/* Uso */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-gray-600">
                          <Image className="w-4 h-4" />
                          {imagesUsed}
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <Video className="w-4 h-4" />
                          {videosUsed}
                        </span>
                      </div>
                    </td>

                    {/* Criado em */}
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{helpers.formatRelativeDate(user.created_at)}</p>
                      <p className="text-xs text-gray-400">{helpers.formatDate(user.created_at)}</p>
                    </td>

                    {/* Ações */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {actionLoading === user.id ? (
                          <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                        ) : (
                          <>
                            <button
                              onClick={() => handleViewDetails(user)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => { setSelectedUser(user); setShowCreditsModal(true); }}
                              className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                              title="Adicionar créditos"
                            >
                              <Plus className="w-4 h-4 text-green-600" />
                            </button>
                            <button
                              onClick={() => { setSelectedUser(user); setShowSubscriptionModal(true); }}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Gerenciar assinatura"
                            >
                              <CreditCard className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => handleResetUsage(user)}
                              className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Resetar uso"
                            >
                              <RotateCcw className="w-4 h-4 text-orange-600" />
                            </button>
                            <button
                              onClick={() => handleToggleAdmin(user)}
                              className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                              title={user.is_admin ? 'Remover admin' : 'Promover a admin'}
                            >
                              {user.is_admin ? (
                                <ShieldOff className="w-4 h-4 text-purple-600" />
                              ) : (
                                <Shield className="w-4 h-4 text-purple-600" />
                              )}
                            </button>
                            <button
                              onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Excluir usuário"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Mostrando {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} de {pagination.total} usuários
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
              disabled={pagination.offset === 0}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-4 py-2 text-sm text-gray-600">
              Página {Math.floor(pagination.offset / pagination.limit) + 1} de {Math.ceil(pagination.total / pagination.limit)}
            </span>
            <button
              onClick={() => handlePageChange(pagination.offset + pagination.limit)}
              disabled={!pagination.hasMore}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER - MODAIS
  // ============================================

  const renderDetailsModal = () => {
    if (!showDetailsModal || !selectedUser) return null;

    const user = selectedUser;
    const planInfo = helpers.formatPlan(user.subscription?.plan);
    const statusInfo = helpers.formatSubscriptionStatus(user.subscription?.status, user.subscription?.plan);

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetailsModal(false)}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Detalhes do Usuário</h2>
            <button onClick={() => setShowDetailsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Info básica */}
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${helpers.getAvatarColor(user.name)} flex items-center justify-center text-white font-bold text-xl`}>
                {helpers.getInitials(user.name)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-bold text-gray-900">{user.name}</h3>
                  {user.is_admin && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                      👑 Admin
                    </span>
                  )}
                </div>
                <p className="text-gray-500 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </p>
              </div>
            </div>

            {/* Assinatura */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gray-600" />
                Assinatura
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Plano</p>
                  <p className="font-semibold text-gray-900">{planInfo.emoji} {planInfo.text}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-semibold text-gray-900">{statusInfo.emoji} {statusInfo.text}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Período atual</p>
                  <p className="font-semibold text-gray-900">
                    {user.subscription?.current_period_end 
                      ? `Até ${helpers.formatDate(user.subscription.current_period_end)}`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Trial termina</p>
                  <p className="font-semibold text-gray-900">
                    {user.subscription?.trial_end 
                      ? helpers.formatDate(user.subscription.trial_end)
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Uso */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-600" />
                Uso no Período
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Image className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{user.usage?.images_generated || 0}</p>
                    <p className="text-sm text-gray-500">Imagens geradas</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                    <Video className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{user.usage?.videos_generated || 0}</p>
                    <p className="text-sm text-gray-500">Vídeos gerados</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Datas */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-600" />
                Informações
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Criado em</p>
                  <p className="font-semibold text-gray-900">{helpers.formatDate(user.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Último login</p>
                  <p className="font-semibold text-gray-900">
                    {user.last_login_at ? helpers.formatDate(user.last_login_at) : 'Nunca'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Onboarding</p>
                  <p className="font-semibold text-gray-900">
                    {user.onboarding_completed ? '✅ Completo' : '⏳ Pendente'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ID</p>
                  <p className="font-mono text-xs text-gray-600 truncate">{user.id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => setShowDetailsModal(false)}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreditsModal = () => {
    if (!showCreditsModal || !selectedUser) return null;

    return (
      <CreditsModal
        user={selectedUser}
        onClose={() => { setShowCreditsModal(false); setSelectedUser(null); }}
        onSuccess={() => { loadUsers(); showSuccess('Créditos adicionados com sucesso!'); }}
      />
    );
  };

  const renderSubscriptionModal = () => {
    if (!showSubscriptionModal || !selectedUser) return null;

    return (
      <SubscriptionModal
        user={selectedUser}
        onClose={() => { setShowSubscriptionModal(false); setSelectedUser(null); }}
        onSuccess={() => { loadUsers(); showSuccess('Assinatura atualizada com sucesso!'); }}
      />
    );
  };

  const renderDeleteModal = () => {
    if (!showDeleteModal || !selectedUser) return null;

    return (
      <DeleteModal
        user={selectedUser}
        onClose={() => { setShowDeleteModal(false); setSelectedUser(null); }}
        onSuccess={() => { loadUsers(); loadStatistics(); showSuccess('Usuário excluído com sucesso!'); }}
      />
    );
  };

  // ============================================
  // RENDER PRINCIPAL
  // ============================================

  return (
    <div className="space-y-6">
      {/* Mensagem de sucesso */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-slideIn">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-7 h-7 text-amber-500" />
            Gerenciamento de Usuários
          </h1>
          <p className="text-gray-500 mt-1">Visualize e gerencie todos os usuários da plataforma</p>
        </div>
      </div>

      {/* Toggle Listagem / Analytics */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveView('listagem')}
          className={`px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
            activeView === 'listagem'
              ? 'bg-amber-500 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Users className="w-4 h-4" />
          Listagem
        </button>
        <button
          onClick={() => {
            setActiveView('analytics');
            if (!analyticsData) loadAnalytics();
          }}
          className={`px-4 py-2 rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
            activeView === 'analytics'
              ? 'bg-amber-500 text-white shadow-sm'
              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Analytics
        </button>
      </div>

      {activeView === 'listagem' ? (
        <>
          {/* Cards de estatísticas */}
          {renderStatsCards()}

          {/* Filtros */}
          {renderFilters()}

          {/* Tabela de usuários */}
          {renderUsersTable()}
        </>
      ) : (
        <UsersAnalyticsDashboard data={analyticsData} isLoading={isLoadingAnalytics} />
      )}

      {/* Modais */}
      {renderDetailsModal()}
      {renderCreditsModal()}
      {renderSubscriptionModal()}
      {renderDeleteModal()}
    </div>
  );
};


// ============================================
// COMPONENTE: MODAL DE CRÉDITOS
// ============================================

const CreditsModal = ({ user, onClose, onSuccess }) => {
  const [credits, setCredits] = useState({ images: 0, videos: 0, reason: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (credits.images === 0 && credits.videos === 0) {
      setError('Informe pelo menos uma quantidade de créditos');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await adminUsersService.addCredits(user.id, credits);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="w-6 h-6 text-green-500" />
            Adicionar Créditos
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600">
            Adicionar créditos extras para <strong>{user.name}</strong>
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagens extras
            </label>
            <input
              type="number"
              min="0"
              max="1000"
              value={credits.images}
              onChange={(e) => setCredits(prev => ({ ...prev, images: parseInt(e.target.value) || 0 }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vídeos extras
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={credits.videos}
              onChange={(e) => setCredits(prev => ({ ...prev, videos: parseInt(e.target.value) || 0 }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo (opcional)
            </label>
            <textarea
              value={credits.reason}
              onChange={(e) => setCredits(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Ex: Compensação por problema técnico"
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 resize-none"
              rows={2}
            />
          </div>

          <div className="bg-green-50 rounded-xl p-4">
            <p className="text-sm text-green-700">
              <Info className="w-4 h-4 inline mr-1" />
              Os créditos serão somados ao limite do plano atual.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-6 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Adicionar
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================
// COMPONENTE: MODAL DE ASSINATURA
// ============================================

const SubscriptionModal = ({ user, onClose, onSuccess }) => {
  const [data, setData] = useState({
    plan: '',
    status: '',
    extend_days: '',
    reset_usage: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!data.plan && !data.status && !data.extend_days && !data.reset_usage) {
      setError('Selecione pelo menos uma alteração');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await adminUsersService.updateSubscription(user.id, {
        plan: data.plan || null,
        status: data.status || null,
        extend_days: data.extend_days ? parseInt(data.extend_days) : null,
        reset_usage: data.reset_usage
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-blue-500" />
            Gerenciar Assinatura
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600">
            Atualizar assinatura de <strong>{user.name}</strong>
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alterar plano
            </label>
            <select
              value={data.plan}
              onChange={(e) => setData(prev => ({ ...prev, plan: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Manter atual ({user.subscription?.plan || 'free'})</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="max">Max</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alterar status
            </label>
            <select
              value={data.status}
              onChange={(e) => setData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Manter atual ({user.subscription?.status || 'inactive'})</option>
              <option value="active">Ativo</option>
              <option value="trialing">Trial</option>
              <option value="canceled">Cancelado</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estender período (dias)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={data.extend_days}
              onChange={(e) => setData(prev => ({ ...prev, extend_days: e.target.value }))}
              placeholder="Ex: 30"
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="reset_usage"
              checked={data.reset_usage}
              onChange={(e) => setData(prev => ({ ...prev, reset_usage: e.target.checked }))}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="reset_usage" className="text-sm text-gray-700">
              Resetar contadores de uso
            </label>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              <strong>Atenção:</strong> Esta é uma alteração manual que NÃO afeta o Stripe.
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================
// COMPONENTE: MODAL DE EXCLUSÃO
// ============================================

const DeleteModal = ({ user, onClose, onSuccess }) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (confirmEmail.toLowerCase() !== user.email.toLowerCase()) {
      setError('O email digitado não confere');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await adminUsersService.deleteUser(user.id, confirmEmail);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-red-600 flex items-center gap-2">
            <Trash2 className="w-6 h-6" />
            Excluir Usuário
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-800 font-semibold mb-2">⚠️ Atenção!</p>
            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
              <li>Esta ação é <strong>irreversível</strong></li>
              <li>Todos os dados do usuário serão perdidos</li>
              <li>A subscription no Stripe NÃO será cancelada automaticamente</li>
            </ul>
          </div>

          <p className="text-gray-600">
            Você está prestes a excluir o usuário:
          </p>
          
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="font-bold text-gray-900">{user.name}</p>
            <p className="text-gray-500">{user.email}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Digite o email do usuário para confirmar:
            </label>
            <input
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user.email}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || confirmEmail.toLowerCase() !== user.email.toLowerCase()}
            className="px-6 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Excluir Permanentemente
          </button>
        </div>
      </div>
    </div>
  );
};


export default UsersManagement;